import { useCallback, useEffect, useMemo, useState } from 'react';
import adminApiClient from '@/services/adminApiClient';
import { marked } from 'marked';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PostSummary {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  cover_image_url: string | null;
  author_name: string;
  tags: string[];
  status: 'draft' | 'published';
  published_at: string | null;
  seo_title: string | null;
  seo_description: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface Post extends PostSummary {
  body_md: string;
}

interface ListResponse {
  total: number;
  limit: number;
  offset: number;
  posts: PostSummary[];
}

type Tab = 'all' | 'published' | 'draft';

const PAGE_SIZE = 20;
const SITE = 'https://erodovai.com';

// Google truncates outside these bands; the counters below colour against them.
const TITLE_BAND = [50, 60] as const;
const DESC_BAND = [120, 160] as const;

const EMPTY: Post = {
  id: '', slug: '', title: '', excerpt: '', body_md: '',
  cover_image_url: null, author_name: '', tags: [], status: 'draft',
  published_at: null, seo_title: null, seo_description: null,
  created_at: null, updated_at: null,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const api = {
  list: (params: Record<string, unknown>) =>
    adminApiClient.get('/blog', { params }).then(r => r.data as ListResponse),
  get: (id: string) => adminApiClient.get(`/blog/${id}`).then(r => r.data as Post),
  create: (data: Partial<Post>) => adminApiClient.post('/blog', data).then(r => r.data as Post),
  update: (id: string, data: Partial<Post>) =>
    adminApiClient.patch(`/blog/${id}`, data).then(r => r.data as Post),
  remove: (id: string) => adminApiClient.delete(`/blog/${id}`).then(r => r.data),
};

const slugify = (v: string) =>
  v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 120);

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/** Server messages are nested differently per failure; surface the useful one. */
function errText(err: any, fallback: string): string {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return `${d[0].loc?.slice(-1)[0] ?? 'field'}: ${d[0].msg}`;
  return err?.message || fallback;
}

function Counter({ value, band }: { value: number; band: readonly [number, number] }) {
  const ok = value >= band[0] && value <= band[1];
  return (
    <span className={`text-xs tabular-nums ${value === 0 ? 'text-slate-400' : ok ? 'text-emerald-600' : 'text-amber-600'}`}>
      {value} / {band[0]}–{band[1]}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const published = status === 'published';
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
      published ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
    }`}>
      {published ? 'Published' : 'Draft'}
    </span>
  );
}

// ─── Editor ───────────────────────────────────────────────────────────────────

function Editor({ post, onClose, onSaved }: {
  post: Post;
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const isNew = !post.id;
  const [form, setForm] = useState<Post>(post);
  const [tagInput, setTagInput] = useState((post.tags ?? []).join(', '));
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only touched once the user edits it, so retitling a draft keeps the slug in
  // step while an existing post's URL stays put.
  const [slugLocked, setSlugLocked] = useState(!isNew);

  const set = <K extends keyof Post>(k: K, v: Post[K]) => setForm(f => ({ ...f, [k]: v }));

  const previewHtml = useMemo(
    () => (form.body_md ? (marked.parse(form.body_md, { async: false, gfm: true }) as string) : ''),
    [form.body_md],
  );

  async function save(status?: 'draft' | 'published') {
    setError(null);
    const tags = tagInput.split(',').map(t => t.trim()).filter(Boolean);
    const payload: Partial<Post> = {
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      body_md: form.body_md,
      slug: form.slug || slugify(form.title),
      cover_image_url: form.cover_image_url || null,
      author_name: form.author_name || undefined,
      tags,
      status: status ?? form.status,
      seo_title: form.seo_title || null,
      seo_description: form.seo_description || null,
    };
    if (!payload.title || !payload.excerpt || !payload.body_md) {
      setError('Title, excerpt and body are all required.');
      return;
    }
    setSaving(true);
    try {
      const saved = isNew ? await api.create(payload) : await api.update(post.id, payload);
      onSaved(`“${saved.title}” saved as ${saved.status}.`);
    } catch (err) {
      setError(errText(err, 'Could not save the post.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">{isNew ? 'New post' : 'Edit post'}</h2>
            {!isNew && (
              <p className="text-xs text-slate-400 mt-0.5">
                Updated {fmt(form.updated_at)} · <code>/blog/{form.slug}</code>
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
            <input
              value={form.title}
              onChange={e => {
                set('title', e.target.value);
                if (!slugLocked) set('slug', slugify(e.target.value));
              }}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="Choosing Signs in Kirchhoff's Law"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-slate-700">Slug</label>
              <span className="text-xs text-slate-400">{SITE}/blog/{form.slug || '…'}</span>
            </div>
            <input
              value={form.slug}
              onChange={e => { setSlugLocked(true); set('slug', slugify(e.target.value)); }}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
            {!isNew && (
              <p className="text-xs text-amber-600 mt-1">
                Changing this changes the post's URL and breaks any existing link to it.
              </p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-slate-700">Excerpt</label>
              <Counter value={form.excerpt.length} band={DESC_BAND} />
            </div>
            <textarea
              value={form.excerpt}
              onChange={e => set('excerpt', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="Shown in the listing and used as the meta description."
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-slate-700">Body (Markdown)</label>
              <button
                onClick={() => setPreview(p => !p)}
                className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
              >
                {preview ? 'Edit' : 'Preview'}
              </button>
            </div>
            {preview ? (
              <div
                className="blog-preview min-h-[18rem] px-4 py-3 border border-slate-200 rounded-lg text-sm overflow-y-auto max-h-[28rem]"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            ) : (
              <textarea
                value={form.body_md}
                onChange={e => set('body_md', e.target.value)}
                rows={14}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-400"
                placeholder={'## Why signs go wrong\n\nPick a loop direction **first**…'}
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tags</label>
              <input
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                placeholder="Physics, Current Electricity"
              />
              <p className="text-xs text-slate-400 mt-1">Comma separated.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Author</label>
              <input
                value={form.author_name}
                onChange={e => set('author_name', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                placeholder="Erodov AI Tutor"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Cover image URL</label>
            <input
              value={form.cover_image_url ?? ''}
              onChange={e => set('cover_image_url', e.target.value || null)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="https://…"
            />
          </div>

          <details className="border border-slate-200 rounded-lg">
            <summary className="px-4 py-2.5 text-sm font-medium text-slate-700 cursor-pointer select-none">
              SEO overrides
            </summary>
            <div className="px-4 pb-4 pt-1 space-y-4">
              <p className="text-xs text-slate-500">
                Left blank, the page uses the title and excerpt above. Set these only when the
                search listing should read differently from the page.
              </p>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-slate-700">SEO title</label>
                  <Counter value={(form.seo_title ?? '').length} band={TITLE_BAND} />
                </div>
                <input
                  value={form.seo_title ?? ''}
                  onChange={e => set('seo_title', e.target.value || null)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium text-slate-700">SEO description</label>
                  <Counter value={(form.seo_description ?? '').length} band={DESC_BAND} />
                </div>
                <textarea
                  value={form.seo_description ?? ''}
                  onChange={e => set('seo_description', e.target.value || null)}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </div>
          </details>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-xl">
          <span className="text-xs text-slate-500">
            {form.status === 'published'
              ? 'Live now. Crawlable within two hours of the next scheduled build.'
              : 'Drafts are not visible on the site.'}
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800">
              Cancel
            </button>
            <button
              onClick={() => save('draft')}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-white disabled:opacity-50"
            >
              Save draft
            </button>
            <button
              onClick={() => save('published')}
              disabled={saving}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : form.status === 'published' ? 'Update' : 'Publish'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Blog() {
  const [tab, setTab] = useState<Tab>('all');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<Post | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = { limit: PAGE_SIZE, offset };
      if (tab !== 'all') params.status = tab;
      if (q.trim()) params.q = q.trim();
      setData(await api.list(params));
    } catch (err) {
      setError(errText(err, 'Could not load posts.'));
    } finally {
      setLoading(false);
    }
  }, [tab, q, offset]);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  useEffect(() => { setOffset(0); }, [tab, q]);

  async function openEditor(id?: string) {
    if (!id) return setEditing({ ...EMPTY });
    setBusyId(id);
    try {
      setEditing(await api.get(id));
    } catch (err) {
      setError(errText(err, 'Could not open the post.'));
    } finally {
      setBusyId(null);
    }
  }

  async function toggleStatus(p: PostSummary) {
    const next = p.status === 'published' ? 'draft' : 'published';
    if (next === 'draft' && !confirm(`Unpublish “${p.title}”? It will disappear from the site.`)) return;
    setBusyId(p.id);
    try {
      await api.update(p.id, { status: next });
      setNotice(`“${p.title}” is now ${next}.`);
      await load();
    } catch (err) {
      setError(errText(err, 'Could not change the status.'));
    } finally {
      setBusyId(null);
    }
  }

  async function remove(p: PostSummary) {
    if (!confirm(`Delete “${p.title}” permanently? This cannot be undone.\n\nTo take it off the site but keep it, unpublish instead.`)) return;
    setBusyId(p.id);
    try {
      await api.remove(p.id);
      setNotice(`“${p.title}” deleted.`);
      await load();
    } catch (err) {
      setError(errText(err, 'Could not delete the post.'));
    } finally {
      setBusyId(null);
    }
  }

  const posts = data?.posts ?? [];
  const total = data?.total ?? 0;
  const pages = Math.ceil(total / PAGE_SIZE);
  const page = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Blog</h1>
          <p className="text-sm text-slate-500 mt-1">
            Posts published at <code>{SITE}/blog</code>. A published post is live immediately and
            becomes crawlable within two hours.
          </p>
        </div>
        <button
          onClick={() => openEditor()}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
        >
          New post
        </button>
      </div>

      <div className="flex items-center justify-between gap-4 border-b border-slate-200">
        <div className="flex gap-1">
          {(['all', 'published', 'draft'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                tab === t ? 'border-b-2 border-indigo-500 text-indigo-600' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search titles…"
          className="mb-2 px-3 py-1.5 border border-slate-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />
      </div>

      {notice && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-4 py-3 rounded-lg flex justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-emerald-500 hover:text-emerald-700">×</button>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">×</button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16 text-slate-400 text-sm">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-slate-400 text-sm gap-2">
          <span className="text-3xl">📝</span>
          <span>{q ? 'No posts match that search' : `No ${tab === 'all' ? '' : tab} posts yet`}</span>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-5 py-3 text-left">Title</th>
                <th className="px-5 py-3 text-left">Tags</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Published</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {posts.map(p => (
                <tr key={p.id} className={`hover:bg-slate-50/50 transition-colors ${busyId === p.id ? 'opacity-50' : ''}`}>
                  <td className="px-5 py-3.5">
                    <div className="font-medium text-slate-800">{p.title}</div>
                    <div className="text-xs text-slate-400 font-mono">/blog/{p.slug}</div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-wrap gap-1">
                      {(p.tags ?? []).slice(0, 3).map(t => (
                        <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-3.5"><StatusBadge status={p.status} /></td>
                  <td className="px-5 py-3.5 text-slate-500">{fmt(p.published_at)}</td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-3">
                      {p.status === 'published' && (
                        <a
                          href={`${SITE}/blog/${p.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-slate-500 hover:text-slate-700"
                        >
                          View
                        </a>
                      )}
                      <button
                        onClick={() => openEditor(p.id)}
                        disabled={busyId === p.id}
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => toggleStatus(p)}
                        disabled={busyId === p.id}
                        className="text-xs font-medium text-slate-600 hover:text-slate-800"
                      >
                        {p.status === 'published' ? 'Unpublish' : 'Publish'}
                      </button>
                      <button
                        onClick={() => remove(p)}
                        disabled={busyId === p.id}
                        className="text-xs font-medium text-red-600 hover:text-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between">
          <button
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-white"
          >
            Previous
          </button>
          <span className="text-sm text-slate-500">Page {page} of {pages} · {total} posts</span>
          <button
            disabled={page >= pages}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-white"
          >
            Next
          </button>
        </div>
      )}

      {editing && (
        <Editor
          post={editing}
          onClose={() => setEditing(null)}
          onSaved={msg => { setEditing(null); setNotice(msg); load(); }}
        />
      )}
    </div>
  );
}
